import {
  Component,
  type ErrorInfo,
  type ReactNode,
} from 'react';
import { Button, Notice, Surface } from '../components';
import styles from './RuntimeErrorBoundary.module.css';
import {
  createIncidentId,
  reportRenderFailure,
} from './runtimeDiagnostics';

type RuntimeErrorBoundaryProps = {
  children: ReactNode;
  reloadPage?: (() => void) | undefined;
};

type RuntimeErrorBoundaryState = {
  failed: boolean;
  incidentId?: string | undefined;
};

function reloadWindow(): void {
  window.location.reload();
}

/**
 * React 19 still requires an Error Boundary class to catch descendant render
 * failures and pair a fallback with production reporting.
 * Source: https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */
export class RuntimeErrorBoundary extends Component<
  RuntimeErrorBoundaryProps,
  RuntimeErrorBoundaryState
> {
  override state: RuntimeErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): RuntimeErrorBoundaryState {
    return { failed: true, incidentId: createIncidentId() };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    reportRenderFailure({
      incidentId: this.state.incidentId ?? createIncidentId(),
      error,
      componentStack: info.componentStack ?? undefined,
    });
  }

  override render(): ReactNode {
    if (!this.state.failed) {
      return this.props.children;
    }

    const incidentId = this.state.incidentId ?? 'unavailable';
    const reloadPage = this.props.reloadPage ?? reloadWindow;

    return (
      <main className={styles.viewport}>
        <Surface className={styles.panel} tone="raised">
          <p className={styles.eyebrow}>Runtime recovery</p>
          <h1>GrowHub hit an unexpected problem</h1>
          <p className={styles.copy}>
            Your existing saved records remain on the server. Reload GrowHub
            to return to a clean screen.
          </p>
          <Notice tone="warning" role="alert">
            Unsaved changes on this screen may need to be entered again.
          </Notice>
          <p className={styles.reference}>
            <span>Support reference</span>
            <code>{incidentId}</code>
          </p>
          <div className={styles.actions}>
            <Button variant="primary" onClick={reloadPage}>
              Reload GrowHub
            </Button>
          </div>
        </Surface>
      </main>
    );
  }
}
